import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { notifyMissionsChanged } from '../lib/mission/missionsChanged';

/**
 * « Relancer » une mission techniquement calée (retries épuisés, boîte mail absente,
 * panne). Passe par l'action `relaunch` de l'Edge `work-agent-threads` — la même façade
 * que le reste du runtime : l'app n'écrit jamais `work_agent_*` en direct, le statut→actif
 * et l'action `pending` sont décidés côté serveur.
 *
 * Même contrat d'enveloppe que `useMissionActions` : on ne considère le geste réussi
 * que si l'Edge répond `success !== false`, et l'appelant refetch derrière.
 */
interface EdgeEnvelope {
  success?: boolean;
  error?: string;
}

export function useRelaunchMission(onChanged?: () => void) {
  const [pendingThreadId, setPendingThreadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const relaunch = useCallback(
    async (threadId: string): Promise<boolean> => {
      setPendingThreadId(threadId);
      setError(null);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke<EdgeEnvelope>(
          'work-agent-threads',
          { body: { action: 'relaunch', threadId } },
        );
        if (invokeError) throw invokeError;
        if (data?.success === false) throw new Error(data.error ?? 'Relance impossible');
        onChanged?.();
        // The relaunched mission moves from « Otto a calé » to « Otto travaille » and
        // gets a journal row: every reader reloads its own source.
        notifyMissionsChanged();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      } finally {
        setPendingThreadId(null);
      }
    },
    [onChanged],
  );

  return { relaunch, pendingThreadId, error };
}
